import { useState } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TablePagination,
    Stack,
    CircularProgress
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
    PersonAddOutlined,
    PaymentsOutlined,
    Visibility,
    MoreVertOutlined,
    EditOutlined,
    PersonOffOutlined,
    ReceiptLongOutlined,
    Search,
    FilterAltOutlined,
    RestartAlt,
    CalendarToday,
    History,
    AssignmentIndOutlined,
    FileDownloadOutlined
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TextField, InputAdornment, FormControl, InputLabel, Select, Button, Collapse, Grid, Tooltip } from '@mui/material';

import { fadeUp, getRoleColor, getRoleLabel } from '../utils/dashboardUtils';

const getStatusChip = (type, value) => {
    if (!value)
        return (
            <Typography variant="body2" color="text.secondary">
                N/A
            </Typography>
        );

    const normalizedValue = value.toString().trim();
    const lowerValue = normalizedValue.toLowerCase();

    const statusMap = {
        bgv: {
            verified: 'success.main',
            'not verified': 'error.main',
            'on progress': 'info.main',
            rejected: 'error.main'
        },
        training: {
            completed: 'success.main',
            'not completed': 'error.main',
            ongoing: 'info.main'
        }
    };

    const color = statusMap[type]?.[lowerValue] || 'text.secondary';

    return (
        <Typography variant="body2" fontWeight={600} color={color}>
            {normalizedValue}
        </Typography>
    );
};

const EmployeeActionMenu = ({
    emp,
    onOpenSalaryModal,
    onOpenDetailsModal,
    onOpenQuickView,
    onOpenDeactivationModal,
    onOpenSalaryListModal
}) => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{ color: 'text.secondary', bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}
            >
                <MoreVertOutlined fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    elevation: 3,
                    sx: { minWidth: 160, borderRadius: 2, mt: 0.5 }
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenDetailsModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <Visibility fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>View Details</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenQuickView(emp);
                    }}
                >
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Manage Portals</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSalaryModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <PaymentsOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Salary Payment</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenSalaryListModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <ReceiptLongOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Salary List</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={emp.status !== 'Active'}
                    onClick={() => {
                        handleClose();
                        navigate('/HR/add-sales-employee', { state: { editEmployeeData: emp } });
                    }}
                >
                    <ListItemIcon>
                        <EditOutlined fontSize="small" color={emp.status !== 'Active' ? 'disabled' : 'action'} />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}>Edit Employee</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        onOpenDeactivationModal(emp);
                    }}
                >
                    <ListItemIcon>
                        <PersonOffOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 500, color: 'error.main' }}>
                        Deactivate Employee
                    </ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

const EmployeeTable = ({
    employees,
    filteredEmployees,
    activeRole,
    setActiveRole,
    onOpenSalaryModal,
    onOpenDetailsModal,
    onOpenQuickView,
    onOpenDeactivationModal,
    onOpenSalaryListModal,
    onOpenStatusModal,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    onExport,
    // Filter Props
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    startDoj,
    setStartDoj,
    endDoj,
    setEndDoj,
    reportingManager,
    setReportingManager,
    trainingStatus,
    setTrainingStatus,
    bgvStatus,
    setBgvStatus,
    setPage
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(0);
    };

    const clearFilters = () => {
        setSearch('');
        setStatusFilter('');
        setStartDoj('');
        setEndDoj('');
        setReportingManager('');
        setTrainingStatus('');
        setBgvStatus('');
        setPage(0);
    };

    const hasActiveFilters = search || statusFilter || startDoj || endDoj || reportingManager || trainingStatus || bgvStatus;
    const handleExportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const allEmployees = onExport ? await onExport() : employees;
            const dataToExport = allEmployees.map((emp) => ({
                ID: emp.id,
                'Employee ID': emp.employee_id,
                Name: emp.name,
                Email: emp.email,
                Phone: emp.phone,
                PAN: emp.pan,
                Aadhaar: emp.aadhar_number,
                Role: getRoleLabel(emp.role),
                State: emp.stateList || 'NA',
                District: emp.districtList || 'NA',
                City: emp.cityList || 'NA',
                Manager_ID: emp.reporting_manager,
                Offer_Date: emp.date_of_offer || 'NA',
                Joining_Date: emp.date_of_joining || 'NA',
                Effective_Date: emp.effective_employment_date || 'NA',
                Status: emp.status,
                BGV_Status: emp.bgv_status,
                Training_Status: emp.training_status
            }));

            const worksheet = XLSX.utils.json_to_sheet(dataToExport);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
            XLSX.writeFile(workbook, `Employee_Directory_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <motion.div custom={5} variants={fadeUp} initial="hidden" animate="visible">
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box
                    sx={{
                        px: 3,
                        pt: 2.5,
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700}>
                                Employee Directory
                            </Typography>
                            {hasActiveFilters && (
                                <Chip
                                    label="Filtered"
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {activeRole
                                ? `Showing ${filteredEmployees.length} employees — ${getRoleLabel(activeRole)}`
                                : `Showing ${filteredEmployees.length} of ${totalCount} employees`}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            placeholder="Search Name, Email, Phone..."
                            size="small"
                            value={search}
                            onChange={handleFilterChange(setSearch)}
                            sx={{
                                width: { xs: '100%', sm: 260 },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: '#f8fafc',
                                    '& fieldset': { borderColor: 'divider' }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                                    </InputAdornment>
                                )
                            }}
                        />

                        <Tooltip title={showFilters ? 'Hide Filters' : 'Show Advanced Filters'}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                color={showFilters || hasActiveFilters ? 'primary' : 'default'}
                                sx={{
                                    bgcolor: showFilters || hasActiveFilters ? 'primary.lighter' : '#f8fafc',
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.light' : 'divider',
                                    borderRadius: 2
                                }}
                            >
                                <FilterAltOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {hasActiveFilters && (
                            <Tooltip title="Reset Filters">
                                <IconButton
                                    onClick={clearFilters}
                                    sx={{
                                        bgcolor: '#fff1f2',
                                        color: 'error.main',
                                        border: '1px solid',
                                        borderColor: '#fecdd3',
                                        borderRadius: 2,
                                        '&:hover': { bgcolor: '#ffe4e6' }
                                    }}
                                >
                                    <RestartAlt fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Button
                            startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: '18px !important' }} />}
                            variant="outlined"
                            color="primary"
                            onClick={handleExportExcel}
                            disabled={isExporting}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                                borderColor: 'divider',
                                color: 'text.primary',
                                bgcolor: '#fff',
                                '&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.main' }
                            }}
                        >
                            {isExporting ? 'Exporting...' : 'Export Excel'}
                        </Button>

                        <Button
                            startIcon={<PersonAddOutlined sx={{ fontSize: '18px !important' }} />}
                            variant="contained"
                            color="primary"
                            href="/HR/add-sales-employee"
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)'
                            }}
                        >
                            Add Employee
                        </Button>
                    </Box>
                </Box>

                <Collapse in={showFilters}>
                    <Divider />
                    <Box sx={{ p: 3, bgcolor: '#f8fafc' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="status-filter-label">Status</InputLabel>
                                    <Select
                                        labelId="status-filter-label"
                                        label="Status"
                                        value={statusFilter}
                                        onChange={handleFilterChange(setStatusFilter)}
                                        sx={{ borderRadius: 2, bgcolor: '#fff' }}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="Deactive">Deactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="training-filter-label">Training</InputLabel>
                                    <Select
                                        labelId="training-filter-label"
                                        label="Training"
                                        value={trainingStatus}
                                        onChange={handleFilterChange(setTrainingStatus)}
                                        sx={{ borderRadius: 2, bgcolor: '#fff' }}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                        <MenuItem value="Not Completed">Not Completed</MenuItem>
                                        <MenuItem value="Ongoing">Ongoing</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="bgv-filter-label">BGV</InputLabel>
                                    <Select
                                        labelId="bgv-filter-label"
                                        label="BGV"
                                        value={bgvStatus}
                                        onChange={handleFilterChange(setBgvStatus)}
                                        sx={{ borderRadius: 2, bgcolor: '#fff' }}
                                    >
                                        <MenuItem value="">All Status</MenuItem>
                                        <MenuItem value="Verified">Verified</MenuItem>
                                        <MenuItem value="Not Verified">Not Verified</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    label="Manager ID"
                                    size="small"
                                    value={reportingManager}
                                    onChange={handleFilterChange(setReportingManager)}
                                    placeholder="e.g. 144"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AssignmentIndOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Joined From"
                                    size="small"
                                    value={startDoj}
                                    onChange={handleFilterChange(setStartDoj)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Joined To"
                                    size="small"
                                    value={endDoj}
                                    onChange={handleFilterChange(setEndDoj)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
                <Divider />
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                {['Employee', 'Emp ID', 'ID', 'Role', 'State', 'Status', 'BGV', 'Training', 'Joined', 'Actions'].map((h) => (
                                    <TableCell
                                        key={h}
                                        sx={{ fontWeight: 700, fontSize: 12, color: 'text.secondary', py: 1.5 }}
                                        align={h === 'Actions' ? 'center' : 'left'}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <AnimatePresence>
                                {filteredEmployees.map((emp, i) => (
                                    <motion.tr
                                        key={emp.id}
                                        initial={{ opacity: 0, x: -12 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 12 }}
                                        transition={{ delay: i * 0.04, duration: 0.25 }}
                                        style={{ display: 'table-row' }}
                                    >
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                                                        {emp.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}
                                            >
                                                {emp.employee_id || 'NA'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}
                                            >
                                                {emp.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getRoleLabel(emp.role)}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setActiveRole((prev) => (prev === emp.role ? null : emp.role))}
                                                sx={{
                                                    cursor: 'pointer',
                                                    borderColor: `${getRoleColor(emp.role)}40`,
                                                    color: getRoleColor(emp.role),
                                                    bgcolor: `${getRoleColor(emp.role)}08`,
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        bgcolor: `${getRoleColor(emp.role)}15`
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {emp.stateList || 'NA'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={emp.status === 'Active' ? 'Active' : 'Deactivated'}
                                                size="small"
                                                sx={{
                                                    bgcolor: emp.status === 'Active' ? '#9bf2b9' : '#fee2e2',
                                                    color: emp.status === 'Active' ? '#166534' : '#991b1b',
                                                    fontWeight: 700,
                                                    fontSize: '0.65rem',
                                                    height: 20
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getStatusChip('bgv', emp.bgv_status)}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onOpenStatusModal(emp, 'bgv')}
                                                    sx={{
                                                        p: 0.5,
                                                        opacity: 0.3,
                                                        '&:hover': { opacity: 1, bgcolor: 'primary.light', color: 'primary.main' }
                                                    }}
                                                >
                                                    <EditOutlined sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getStatusChip('training', emp.training_status)}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onOpenStatusModal(emp, 'training')}
                                                    sx={{
                                                        p: 0.5,
                                                        opacity: 0.3,
                                                        '&:hover': { opacity: 1, bgcolor: 'primary.light', color: 'primary.main' }
                                                    }}
                                                >
                                                    <EditOutlined sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {emp.date_of_joining || 'NA'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <EmployeeActionMenu
                                                emp={emp}
                                                onOpenDetailsModal={onOpenDetailsModal}
                                                onOpenSalaryModal={onOpenSalaryModal}
                                                onOpenQuickView={onOpenQuickView}
                                                onOpenDeactivationModal={onOpenDeactivationModal}
                                                onOpenSalaryListModal={onOpenSalaryListModal}
                                            />
                                        </TableCell>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                />
            </Paper>
        </motion.div>
    );
};

EmployeeTable.propTypes = {
    employees: PropTypes.array.isRequired,
    filteredEmployees: PropTypes.array.isRequired,
    activeRole: PropTypes.string,
    totalCount: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
    setActiveRole: PropTypes.func.isRequired,
    onOpenSalaryModal: PropTypes.func.isRequired,
    onOpenDetailsModal: PropTypes.func.isRequired,
    onOpenQuickView: PropTypes.func.isRequired,
    onOpenDeactivationModal: PropTypes.func.isRequired,
    onOpenSalaryListModal: PropTypes.func.isRequired,
    onOpenStatusModal: PropTypes.func.isRequired,
    onExport: PropTypes.func,
    search: PropTypes.string,
    setSearch: PropTypes.func.isRequired,
    statusFilter: PropTypes.string,
    setStatusFilter: PropTypes.func.isRequired,
    startDoj: PropTypes.string,
    setStartDoj: PropTypes.func.isRequired,
    endDoj: PropTypes.string,
    setEndDoj: PropTypes.func.isRequired,
    reportingManager: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setReportingManager: PropTypes.func.isRequired,
    trainingStatus: PropTypes.string,
    setTrainingStatus: PropTypes.func.isRequired,
    bgvStatus: PropTypes.string,
    setBgvStatus: PropTypes.func.isRequired,
    setPage: PropTypes.func.isRequired
};

export default EmployeeTable;
